/**
 * Offering-type context + vocabulary hook.
 *
 * A store is either a shop (`goods`) or a practice (`appointments`). Rather than
 * thread `offering_type` through every tab and storefront component as a prop,
 * the store admin and storefront wrap their subtree in <OfferingTypeProvider>,
 * and any descendant pulls the right words with `useVocab()`:
 *
 *   const t = useVocab();
 *   <button>{t.addToCart}</button>   // "הוסף לסל" or "קביעת תור"
 *
 * The actual word maps live in utils/storeVocabulary.js — this file is just the
 * React plumbing around them.
 */
import React, { createContext, useContext, useMemo } from 'react';
import {
    getVocab,
    normalizeOfferingType,
    DEFAULT_OFFERING_TYPE,
    OFFERING_APPOINTMENTS,
} from '../utils/storeVocabulary';

const OfferingTypeContext = createContext(DEFAULT_OFFERING_TYPE);

/** Provide the resolved offering type to a subtree (admin or storefront root). */
export const OfferingTypeProvider = ({ offeringType, children }) => {
    const value = normalizeOfferingType(offeringType);
    return (
        <OfferingTypeContext.Provider value={value}>
            {children}
        </OfferingTypeContext.Provider>
    );
};

/** Raw offering type ('goods' | 'appointments') for the current subtree. */
export const useOfferingType = () => useContext(OfferingTypeContext);

/** True when the current store books appointments rather than selling goods. */
export const useIsAppointments = () => useContext(OfferingTypeContext) === OFFERING_APPOINTMENTS;

/** The resolved vocabulary map for the current subtree. */
export const useVocab = () => {
    const offeringType = useContext(OfferingTypeContext);
    return useMemo(() => getVocab(offeringType), [offeringType]);
};

export default useVocab;
